import { capitalizeTerm } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { Button } from "@loot-os/ui/components/button";
import { Calendar } from "@loot-os/ui/components/calendar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { Popover, PopoverContent, PopoverTrigger } from "@loot-os/ui/components/popover";
import { useQuery } from "@tanstack/react-query";
import { useSearch } from "@tanstack/react-router";
import { CalendarIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { track } from "../lib/analytics.js";
import { seedQueryOptions, verificationQueryOptions } from "../lib/queries.js";
import { calendarDateToDayKey, dayKeyToCalendarDate, utcDayKey } from "../lib/seedDate.js";
import { useTerminology } from "../lib/terminology.js";

/**
 * The public fairness surface — no key or session required:
 * GET /v1/fairness/seeds/:date, GET /v1/opens/:id/verification, and a full
 * in-browser recomputation of the roll via @loot-os/client/verify.
 */
export function FairnessPage() {
  return (
    <div className="grid gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Provably fair</h1>
        <p className="text-sm text-muted-foreground">
          Server seeds are committed by hash before the day starts and revealed after it ends — any
          pull can be recomputed in your browser, no trust required.
        </p>
      </div>
      <VerifyOpenCard />
      <SeedLookupCard />
    </div>
  );
}

function VerifyOpenCard() {
  const { item } = useTerminology();
  const { openId: linkedOpenId } = useSearch({ from: "/fairness" });
  const [openId, setOpenId] = useState(linkedOpenId ?? "");
  const [submittedId, setSubmittedId] = useState(linkedOpenId ?? "");

  // Auto-verify when arriving via a "Verify" link from the open history.
  useEffect(() => {
    if (linkedOpenId) {
      setOpenId(linkedOpenId);
      setSubmittedId(linkedOpenId);
    }
  }, [linkedOpenId]);

  const query = useQuery({
    ...verificationQueryOptions(submittedId || "none"),
    enabled: !!submittedId,
  });
  const busy = query.isFetching;
  const bundle = query.data?.bundle ?? null;
  const result = query.data?.result ?? null;
  const error =
    !busy && query.error
      ? query.error instanceof Error
        ? query.error.message
        : "verification failed"
      : null;

  // One event per verification answered: keyed on the fetch that produced it, so a re-render
  // (or StrictMode's doubled effect) never records the same answer twice.
  const reportedRef = useRef<string | null>(null);
  const answeredAt =
    query.errorUpdatedAt > query.dataUpdatedAt ? query.errorUpdatedAt : query.dataUpdatedAt;
  useEffect(() => {
    if (busy || !submittedId || !answeredAt) return;
    const key = `${submittedId}:${answeredAt}`;
    if (reportedRef.current === key) return;
    reportedRef.current = key;
    track("fairness_verified", {
      outcome: query.error ? "error" : result?.valid ? "valid" : "invalid",
    });
  }, [busy, submittedId, answeredAt, query.error, result]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Verify an open</CardTitle>
        <CardDescription>
          GET /v1/opens/:id/verification, recomputed locally with WebCrypto.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const id = openId.trim();
            if (!id) return;
            // Re-submitting the same id re-verifies rather than serving cache.
            if (id === submittedId) void query.refetch();
            else setSubmittedId(id);
          }}
        >
          <div className="grid min-w-64 flex-1 gap-1.5">
            <Label htmlFor="open-id">Open ID</Label>
            <Input
              id="open-id"
              className="font-mono text-xs"
              placeholder="01K…"
              value={openId}
              onChange={(e) => setOpenId(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={busy || !openId.trim()}>
            {busy ? "Verifying…" : "Verify"}
          </Button>
        </form>

        {error && <ErrorBanner>{error}</ErrorBanner>}

        {bundle && result && (
          <div className="grid gap-3">
            {result.seedRevealed ? (
              <Badge
                className={
                  result.valid
                    ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-500"
                    : "border-destructive/50 bg-destructive/10 text-destructive"
                }
                variant="outline"
              >
                {result.valid ? "✓ Verified — the roll checks out" : "✗ Mismatch — does not verify"}
              </Badge>
            ) : (
              <Badge variant="outline">
                Server seed not yet revealed — verify again after the UTC day ends
              </Badge>
            )}
            <div className="grid gap-1.5 text-sm">
              <Fact
                label={capitalizeTerm(item.singular)}
                value={`${bundle.open.item.name} (index ${bundle.open.itemIndex})`}
              />
              <Fact label="Recorded roll" value={String(bundle.open.result)} mono />
              <Fact
                label="Recomputed roll"
                value={result.computedResult === null ? "—" : String(result.computedResult)}
                mono
              />
              <Fact
                label="Seed hash matches"
                value={
                  result.serverSeedMatchesHash === null
                    ? "—"
                    : result.serverSeedMatchesHash
                      ? "yes"
                      : "NO"
                }
              />
              <Fact label="Client seed" value={bundle.open.clientSeed} mono />
              <Fact label="Salt · nonce" value={`${bundle.salt} · ${bundle.open.nonce}`} mono />
              <Fact label="Seed date" value={bundle.seed.date} mono />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * A seed's UTC day as a calendar in a popover (shadcn's date picker on the shared Calendar).
 * The cells are UTC days: "today" is the UTC one, and the last day offered is tomorrow's, the
 * furthest seed already committed.
 */
function SeedDatePicker({
  id,
  date,
  onChange,
}: {
  id: string;
  date: string;
  onChange: (date: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = dayKeyToCalendarDate(date);
  const now = new Date();
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            id={id}
            type="button"
            variant="outline"
            aria-expanded={open}
            className="w-44 justify-start px-2.5 font-normal"
          />
        }
      >
        <CalendarIcon data-icon="inline-start" className="text-muted-foreground" />
        <span className="font-mono text-xs">{date}</span>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          required
          defaultMonth={selected}
          selected={selected}
          today={dayKeyToCalendarDate(utcDayKey(now))}
          disabled={{ after: dayKeyToCalendarDate(utcDayKey(now, 1)) }}
          onSelect={(picked) => {
            onChange(calendarDateToDayKey(picked));
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

function SeedLookupCard() {
  const [date, setDate] = useState(() => utcDayKey(new Date()));
  const [submittedDate, setSubmittedDate] = useState<string | null>(null);

  const query = useQuery({
    ...seedQueryOptions(submittedDate ?? "none"),
    enabled: !!submittedDate,
  });
  const busy = query.isFetching;
  const seed = query.data ?? null;
  const error =
    !busy && query.error
      ? query.error instanceof Error
        ? query.error.message
        : "lookup failed"
      : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Daily server seed</CardTitle>
        <CardDescription>
          GET /v1/fairness/seeds/:date — hash-committed up front, revealed after the day ends.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (date === submittedDate) void query.refetch();
            else setSubmittedDate(date);
          }}
        >
          <div className="grid gap-1.5">
            <Label htmlFor="seed-date">Date (UTC)</Label>
            <SeedDatePicker id="seed-date" date={date} onChange={setDate} />
          </div>
          <Button type="submit" disabled={busy}>
            {busy ? "Loading…" : "Look up"}
          </Button>
        </form>

        {error && <ErrorBanner>{error}</ErrorBanner>}

        {seed && (
          <div className="grid gap-1.5 text-sm">
            <Fact label="Commitment (sha256)" value={seed.seedHash} mono />
            <Fact
              label="Server seed"
              value={seed.seed ?? "not yet revealed — check back after the UTC day ends"}
              mono={!!seed.seed}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Fact({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5">
      <span className="shrink-0 text-muted-foreground">{label}</span>
      <span className={mono ? "break-all font-mono text-xs" : "break-words"}>{value}</span>
    </div>
  );
}
